import { useState, useRef, useEffect } from 'react';
import { useLanguage, LanguageCode } from '../contexts/LanguageContext';
import { ChevronDown } from 'lucide-react';
import { FlagIcon } from './FlagIcon';

const languages: { code: LanguageCode; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
];

export function LanguageSelector() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === language);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: LanguageCode) => {
    setLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-[#1a1b25] border border-[#27272a] rounded-lg px-4 py-2 text-white hover:border-[#90d977] transition-all cursor-pointer"
        aria-label="Select language"
      >
        <FlagIcon code={currentLanguage?.code || 'en'} className="w-5 h-5" />
        <span className="text-sm hidden sm:inline">{currentLanguage?.label}</span>
        <ChevronDown className={`h-4 w-4 text-[#a1a1aa] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-[#1a1b25] border border-[#27272a] rounded-lg shadow-2xl overflow-hidden z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all cursor-pointer ${
                language === lang.code
                  ? 'bg-[#90d977]/10 text-white border-l-2 border-l-[#90d977]'
                  : 'text-[#a1a1aa] hover:bg-[#0d0d12] hover:text-white'
              }`}
            >
              <FlagIcon code={lang.code} className="w-5 h-5" />
              <span className="text-sm">{lang.label}</span>
              {language === lang.code && (
                <span className="ml-auto text-[#90d977] text-xs">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}